import { QueryClient, QueryClientProvider } from "react-query";
import { Routes, Route, useLocation, matchPath } from "react-router-dom";
import { ToastContainer, cssTransition } from "react-toastify";
import "./App.scss";
import { BrowserRouter } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { GrigoraVersioning } from "./components/versioning/context";
import React, { Suspense, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ConfigProvider } from "antd";
import LoginRequired from "./components/account/loginRequired";
import { Account } from "./components/account/account";
import { DashboardSkeleton } from "./components/dashboard/dashboardSkeleton";
import AuthSkeleton from "./components/skeletons/auth-skeleton";
import NotFoundSkeleton from "./components/skeletons/not-found-skeleton";
import ProjectSkeleton from "./pages/projects/projectSkeleton";
import ProfileSkeleton from "./components/skeletons/profile-skeleton";
import ProjectsSkeleton from "./components/skeletons/projects-skeleton";
import NewProjectSkeleton from "./components/skeletons/new-project-skeleton";
import StartNewProjectSkeleton from "./components/skeletons/start-new-project-skeleton";
import FilesSkeleton from "./components/skeletons/files-skeleton";
import SupportSkeleton from "./components/skeletons/support-skeleton";
import TeamsSkeleton from "./components/skeletons/teams-skeleton";
import TemplateSkeleton from "./components/skeletons/templates-skeleton";
import TodoSkeleton from "./components/skeletons/todo-skeleton";
import SettingsSkeleton from "./components/skeletons/settings-skeleton";
import CategorySkeleton from "./components/skeletons/category-skeleton";
import AddCategorySkeleton from "./components/skeletons/add-category";
import PostsSkeleton from "./components/skeletons/posts-skeleton";
import IntegrationSkeleton from "./components/skeletons/integration-skeleton";
import GrigoraPreviewSkeleton from "./components/skeletons/grigora-preview";
import AddEditPostSkeleton from "./components/skeletons/add-edit-post-skeleton";
import UserDashboardSkeleton from "./components/skeletons/user-dashboard-skeleton";
import AudienceSkeleton from "./components/skeletons/audience-skeleton";
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from "react-router-dom";
import { APIProvider } from "./components/api";
import { WhiteLabelProvider } from "./components/whitelabel";
import { I18nProvider } from "@lingui/react";
import { i18n } from "@lingui/core";

var theme = {
  primary: "#565656",
  verylight: "#f6f6f6",
  lightshadow: "0 0 0 2px rgb(5 5 5 / 20%)",
  light: "#ededed",
  dark: "#565656",
  verydark: "#565656",
};

// import { stopReportingRuntimeErrors } from "react-error-overlay";

// if (process.env.NODE_ENV === "development") {
//   stopReportingRuntimeErrors(); // disables error overlays
// }

// Convert all the above pages into lazy loaded components
const Login = React.lazy(() =>
  import(
    /* webpackChunkName: "page-login" */
    /* webpackMode: "lazy" */
    "./pages/login/login"
  ),
);
const MagicLogin = React.lazy(() =>
  import(
    /* webpackChunkName: "page-magic-login" */
    /* webpackMode: "lazy" */
    "./pages/magic-login/magic-login"
  ),
);
const Logout = React.lazy(() =>
  import(
    /* webpackChunkName: "page-logout" */
    /* webpackMode: "lazy" */
    "./pages/logout"
  ),
);
const NotFound404 = React.lazy(() =>
  import(
    /* webpackChunkName: "page-404" */
    /* webpackMode: "lazy" */
    "./pages/404/404"
  ),
);
const Profile = React.lazy(() =>
  import(
    /* webpackChunkName: "page-profile" */
    /* webpackMode: "lazy" */
    "./pages/Profile/profile"
  ),
);
const Billing = React.lazy(() =>
  import(
    /* webpackChunkName: "page-billing" */
    /* webpackMode: "lazy" */
    "./pages/Billing/billing"
  ),
);
const SaaszillaRedeem = React.lazy(() =>
  import(
    /* webpackChunkName: "page-saaszilla-redeem" */
    /* webpackMode: "lazy" */
    "./pages/saaszilla-redeem"
  ),
);
const Verify = React.lazy(() =>
  import(
    /* webpackChunkName: "page-verify" */
    /* webpackMode: "lazy" */
    "./pages/verify/verify"
  ),
);
const Register = React.lazy(() =>
  import(
    /* webpackChunkName: "page-register" */
    /* webpackMode: "lazy" */
    "./pages/register/register"
  ),
);

const Projects = React.lazy(() =>
  import(
    /* webpackChunkName: "page-projects" */
    /* webpackMode: "lazy" */
    "./pages/projects/projects"
  ),
);
const Oauth = React.lazy(() =>
  import(
    /* webpackChunkName: "page-oauth" */
    /* webpackMode: "lazy" */
    "./pages/oauth/oauth"
  ),
);
const VerifyCollaboratorInvitation = React.lazy(() =>
  import(
    /* webpackChunkName: "page-verify-collaborator-invitation" */
    /* webpackMode: "lazy" */
    "./pages/verify_collaborator_invitation"
  ),
);
const NewProject = React.lazy(() =>
  import(
    /* webpackChunkName: "page-new-project" */
    /* webpackMode: "lazy" */
    "./pages/new-project"
  ),
);
const StartNewProject = React.lazy(() =>
  import(
    /* webpackChunkName: "page-start-new" */
    /* webpackMode: "lazy" */
    "./pages/start-new"
  ),
);

const StartNewAi = React.lazy(() =>
  import(
    /* webpackChunkName: "page-start-new-ai" */
    /* webpackMode: "lazy" */
    "./pages/start-new/ai"
  ),
);

// Lazy load forgot password
const Home = React.lazy(() =>
  import(
    /* webpackChunkName: "page-home" */
    /* webpackMode: "lazy" */
    "./pages/home/home"
  ),
);
// Lazy load forgot password
const ForgotPassword = React.lazy(() =>
  import(
    /* webpackChunkName: "page-forgotpassword" */
    /* webpackMode: "lazy" */
    "./pages/login/forgotPassword"
  ),
);
const Dashboard = React.lazy(() => import("./pages/dashboard/dashboard"));
const UserDashboard = React.lazy(() =>
  import("./pages/userdashboard/userdashboard"),
);
const Files = React.lazy(() =>
  import(
    /* webpackChunkName: "page-files" */
    /* webpackMode: "lazy" */
    "./pages/files/files"
  ),
);
const Support = React.lazy(() =>
  import(
    /* webpackChunkName: "page-support" */
    /* webpackMode: "lazy" */
    "./pages/support/support"
  ),
);
const Teams = React.lazy(() =>
  import(
    /* webpackChunkName: "page-teams" */
    /* webpackMode: "lazy" */
    "./pages/teams/index"
  ),
);
const Templates = React.lazy(() =>
  import(
    /* webpackChunkName: "page-templates" */
    /* webpackMode: "lazy" */
    "./pages/templates/index"
  ),
);
const Todo = React.lazy(() =>
  import(
    /* webpackChunkName: "page-todo" */
    /* webpackMode: "lazy" */
    "./pages/todo/todo"
  ),
);
const Settings = React.lazy(() =>
  import(
    /* webpackChunkName: "page-settings" */
    /* webpackMode: "lazy" */
    "./pages/settings/settings"
  ),
);
const Categories = React.lazy(() =>
  import(
    /* webpackChunkName: "page-categories" */
    /* webpackMode: "lazy" */
    "./pages/categories/categories"
  ),
);
const EditCategory = React.lazy(() =>
  import(
    /* webpackChunkName: "page-editcategory" */
    /* webpackMode: "lazy" */
    "./pages/categoryedit/editcategory"
  ),
);
const EditPost = React.lazy(() =>
  import(
    /* webpackChunkName: "page-editpost" */
    /* webpackMode: "lazy" */
    "./pages/postEdit/editpost"
  ),
);
const Posts = React.lazy(() =>
  import(
    /* webpackChunkName: "page-posts" */
    /* webpackMode: "lazy" */
    "./pages/posts/posts"
  ),
);
const Forms = React.lazy(() =>
  import(
    /* webpackChunkName: "page-forms" */
    /* webpackMode: "lazy" */
    "./pages/forms"
  ),
);
const Integration = React.lazy(() =>
  import(
    /* webpackChunkName: "page-integration" */
    /* webpackMode: "lazy" */
    "./pages/dashboard/integration/integration"
  ),
);
const GrigoraPreview = React.lazy(() =>
  import(
    /* webpackChunkName: "page-grigora-preview" */
    /* webpackMode: "lazy" */
    "./components/post-preview"
  ),
);

const Audience = React.lazy(() =>
  import(
    /* webpackChunkName: "page-audience" */
    /* webpackMode: "lazy" */
    "./pages/newsletter/newsletter"
  ),
);

const NewsletterUnsubscribe = React.lazy(() =>
  import(
    /* webpackChunkName: "page-newsletter-unsubscribe" */
    /* webpackMode: "lazy" */
    "./pages/newsletter/unsubscribe"
  ),
);

const SeoReport = React.lazy(() =>
  import(
    /* webpackChunkName: "page-seo-report" */
    /* webpackMode: "lazy" */
    "./pages/seo-report/seo-report"
  ),
);

const GoogleAnalyticsReport = React.lazy(() =>
  import(
    /* webpackChunkName: "page-google-analytics-report" */
    /* webpackMode: "lazy" */
    "./pages/google-analytics-report/google-analytics-report"
  ),
);

const KeywordResearch = React.lazy(() =>
  import(
    /* webpackChunkName: "page-keyword-research" */
    /* webpackMode: "lazy" */
    "./pages/keyword-research/keyword-research"
  ),
);

const GoogleSearchConsoleOauth = React.lazy(() =>
  import(
    /* webpackChunkName: "page-google-search-console-oauth" */
    /* webpackMode: "lazy" */
    "./pages/oauth-google-search-console"
  ),
);

const GoogleAnalyticsOauth = React.lazy(() =>
  import(
    /* webpackChunkName: "page-google-analytics-oauth" */
    /* webpackMode: "lazy" */
    "./pages/oauth-google-analytics"
  ),
);

const AdvancedAnalytics = React.lazy(() =>
  import(
    /* webpackChunkName: "page-advanced-analytics" */
    /* webpackMode: "lazy" */
    "./pages/advanced-analytics/advancedAnalytics"
  ),
);

const DirectoryListings = React.lazy(() =>
  import(
    /* webpackChunkName: "page-directory-listings" */
    /* webpackMode: "lazy" */
    "./pages/directory/listings"
  ),
);

const DirectoryEditListing = React.lazy(() =>
  import(
    /* webpackChunkName: "page-directory-edit-listing" */
    /* webpackMode: "lazy" */
    "./pages/directory/edit-listing"
  ),
);

const DirectoryConfiguration = React.lazy(() =>
  import(
    /* webpackChunkName: "page-directory-configuration" */
    /* webpackMode: "lazy" */
    "./pages/directory/directory-configuration"
  ),
);

const DirectoryTypes = React.lazy(() =>
  import(
    /* webpackChunkName: "page-directory-types" */
    /* webpackMode: "lazy" */
    "./pages/directory/directory-types"
  ),
);

const WhiteLabelSettings = React.lazy(() =>
  import(
    /* webpackChunkName: "page-white-label-settings" */
    /* webpackMode: "lazy" */
    "./pages/whitelabel-settings"
  ),
);

const WhiteLabelPurchase = React.lazy(() =>
  import(
    /* webpackChunkName: "page-white-label-purchase" */
    /* webpackMode: "lazy" */
    "./pages/whitelabel-purchase"
  ),
);

const ToolsIndex = React.lazy(() => import("./pages/tools/index"));
const ToolsLogoGenerator = React.lazy(() =>
  import("./pages/tools/logo-generator"),
);
const EmailsLayout = React.lazy(() =>
  import(
    /* webpackChunkName: "page-emails-layout" */
    /* webpackMode: "lazy" */
    "./pages/emails/layout"
  ),
);
const EmailsDashboard = React.lazy(() =>
  import(
    /* webpackChunkName: "page-emails-dashboard" */
    /* webpackMode: "lazy" */
    "./pages/emails/dashboard"
  ),
);
const NewMailbox = React.lazy(() =>
  import(
    /* webpackChunkName: "page-emails-new" */
    /* webpackMode: "lazy" */
    "./pages/emails/new"
  ),
);
const DomainVerification = React.lazy(() =>
  import(
    /* webpackChunkName: "page-emails-verification" */
    /* webpackMode: "lazy" */
    "./pages/emails/verification"
  ),
);
const ConnectApps = React.lazy(() =>
  import(
    /* webpackChunkName: "page-emails-connect" */
    /* webpackMode: "lazy" */
    "./pages/emails/connect"
  ),
);

const AppsumoConnect = React.lazy(() =>
  import(
    /* webpackChunkName: "page-appsumo-connect" */
    /* webpackMode: "lazy" */
    "./pages/appsumo"
  ),
);

const queryClient = new QueryClient();

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        path="/site/:projectID"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<DashboardSkeleton />}>
              <Dashboard />
            </Suspense>
          </LoginRequired>
        }
      >
        <Route
          path="/site/:projectID/"
          exact
          element={
            <Suspense fallback={<UserDashboardSkeleton />}>
              <UserDashboard />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/files"
          exact
          element={
            <Suspense fallback={<FilesSkeleton />}>
              <Files />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/newsletter"
          exact
          element={
            <Suspense fallback={<AudienceSkeleton />}>
              <Audience />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/seo-report"
          exact
          element={
            <Suspense fallback={<AudienceSkeleton />}>
              <SeoReport />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/google-analytics-report"
          exact
          element={
            <Suspense fallback={<AudienceSkeleton />}>
              <GoogleAnalyticsReport />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/advanced-analytics"
          exact
          element={
            <Suspense fallback={<AudienceSkeleton />}>
              <AdvancedAnalytics />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/keyword-research"
          exact
          element={
            <Suspense fallback={<AudienceSkeleton />}>
              <KeywordResearch />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/support"
          exact
          element={
            <Suspense fallback={<SupportSkeleton />}>
              <Support />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/teams/:page"
          exact
          element={
            <Suspense fallback={<TeamsSkeleton />}>
              <Teams />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/templates"
          exact
          element={
            <Suspense fallback={<TemplateSkeleton />}>
              <Templates />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/todo"
          exact
          element={
            <Suspense fallback={<TodoSkeleton />}>
              <Todo />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/settings"
          exact
          element={
            <Suspense fallback={<SettingsSkeleton />}>
              <Settings raw={true} />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/settings/:setting"
          exact
          element={
            <Suspense fallback={<SettingsSkeleton />}>
              <Settings />
            </Suspense>
          }
        />
        <Route path="/site/:projectID/editor" element={<Home />} />
        <Route
          path="/site/:projectID/cms/category"
          exact
          element={
            <Suspense fallback={<CategorySkeleton />}>
              <Categories />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/cms/addcategory"
          exact
          element={
            <Suspense fallback={<AddCategorySkeleton />}>
              <EditCategory newCategory={true} />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/cms/editcategory/:categoryID"
          exact
          element={
            <Suspense fallback={<AddCategorySkeleton />}>
              <EditCategory />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/cms/posts"
          exact
          element={
            <Suspense fallback={<PostsSkeleton />}>
              <Posts />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/cms/addpost"
          exact
          element={
            <Suspense fallback={<AddEditPostSkeleton />}>
              <EditPost newPost={true} />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/cms/editpost/:postID"
          exact
          element={
            <Suspense fallback={<AddEditPostSkeleton />}>
              <EditPost newPost={false} />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/cms/preview/:postID"
          exact
          element={
            <Suspense fallback={<GrigoraPreviewSkeleton />}>
              <GrigoraPreview />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/directory/listings"
          exact
          element={
            <Suspense fallback={<PostsSkeleton />}>
              <DirectoryTypes type="listings" />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/directory/listings/:typeID"
          exact
          element={
            <Suspense fallback={<PostsSkeleton />}>
              <DirectoryListings />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/directory/listings/:typeID/add"
          exact
          element={
            <Suspense fallback={<PostsSkeleton />}>
              <DirectoryEditListing newListing={true} />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/directory/listings/:typeID/edit/:listingID"
          exact
          element={
            <Suspense fallback={<AddEditPostSkeleton />}>
              <DirectoryEditListing />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/directory/configurations"
          exact
          element={
            <Suspense fallback={<AddEditPostSkeleton />}>
              <DirectoryTypes type="configurations" />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/directory/configurations/add"
          exact
          element={
            <Suspense fallback={<PostsSkeleton />}>
              <DirectoryConfiguration />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/directory/configurations/edit/:typeID"
          exact
          element={
            <Suspense fallback={<AddEditPostSkeleton />}>
              <DirectoryConfiguration />
            </Suspense>
          }
        />
        <Route
          path="/site/:projectID/forms"
          exact
          element={
            <Suspense fallback={<PostsSkeleton />}>
              <Forms />
            </Suspense>
          }
        />
        {/* <Route
          path="/site/:projectID/integration"
          exact
          element={
            <Suspense fallback={<IntegrationSkeleton />}>
              <Integration />
            </Suspense>
          }
        /> */}
      </Route>
      <Route
        path="/verify/:id"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <Verify />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="/newsletter-unsubscribe"
        element={
          <APIProvider>
            <Suspense fallback={<></>}>
              <NewsletterUnsubscribe />
            </Suspense>
          </APIProvider>
        }
      />
      <Route
        path="/new"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<NewProjectSkeleton />}>
              <NewProject />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="/start"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<StartNewProjectSkeleton />}>
              <StartNewProject />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="/start/ai"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<StartNewProjectSkeleton />}>
              <StartNewAi />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="/"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<ProjectsSkeleton />}>
              <Projects />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="/profile"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<ProfileSkeleton />}>
              <Profile />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="billing"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <Billing showEnterprise={true} />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="whitelabel"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <WhiteLabelSettings />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="whitelabel/purchase"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <WhiteLabelPurchase />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="tools"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <ToolsIndex />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="tools/logo-generator"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <ToolsLogoGenerator />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="emails"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <EmailsLayout />
            </Suspense>
          </LoginRequired>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<></>}>
              <EmailsDashboard />
            </Suspense>
          }
        />
        <Route
          path="new"
          element={
            <Suspense fallback={<></>}>
              <NewMailbox />
            </Suspense>
          }
        />
        <Route
          path=":domainId/verification"
          element={
            <Suspense fallback={<></>}>
              <DomainVerification />
            </Suspense>
          }
        />
        <Route
          path=":domainId/email/:email/connect"
          element={
            <Suspense fallback={<></>}>
              <ConnectApps />
            </Suspense>
          }
        />
        <Route
          path="domain/:domain/email/:email/connect"
          element={
            <Suspense fallback={<></>}>
              <ConnectApps />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="saaszilla-redeem"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <SaaszillaRedeem />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="login"
        element={
          <LoginRequired not redirect={"/"}>
            <Suspense fallback={<AuthSkeleton />}>
              <Login />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="magic-login"
        element={
          <Suspense fallback={<AuthSkeleton />}>
            <MagicLogin />
          </Suspense>
        }
      />
      <Route
        path="/"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <Projects />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="login"
        element={
          <LoginRequired not redirect={"/"}>
            <Suspense fallback={<></>}>
              <Login />
            </Suspense>
          </LoginRequired>
        }
      />

      <Route
        path="forgot"
        element={
          <LoginRequired not redirect={"/"}>
            <APIProvider>
              <Suspense fallback={<AuthSkeleton screen="forgot" />}>
                <ForgotPassword />
              </Suspense>
            </APIProvider>
          </LoginRequired>
        }
      />

      <Route
        path="oauth"
        element={
          <LoginRequired not redirect={"/"}>
            <Suspense fallback={<></>}>
              <Oauth />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="oauth-google-search-console"
        element={
          <Suspense fallback={<></>}>
            <GoogleSearchConsoleOauth />
          </Suspense>
        }
      />
      <Route
        path="oauth-google-analytics"
        element={
          <Suspense fallback={<></>}>
            <GoogleAnalyticsOauth />
          </Suspense>
        }
      />
      <Route
        path="sign-up"
        element={
          <LoginRequired not redirect={"/"}>
            <APIProvider>
              <Suspense fallback={<AuthSkeleton />}>
                <Register />
              </Suspense>
            </APIProvider>
          </LoginRequired>
        }
      />
      <Route
        path="/appsumo/connect"
        element={
          <APIProvider>
            <Suspense fallback={<></>}>
              <AppsumoConnect />
            </Suspense>
          </APIProvider>
        }
      />
      <Route
        path="logout"
        element={
          <LoginRequired redirect={"/login"}>
            <Suspense fallback={<></>}>
              <Logout />
            </Suspense>
          </LoginRequired>
        }
      />
      <Route
        path="/verify_collaborator_invitation/:id"
        element={
          <Suspense fallback={<></>}>
            <VerifyCollaboratorInvitation />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <Suspense fallback={<NotFoundSkeleton />}>
            <NotFound404 />
          </Suspense>
        }
      />
    </>,
  ),
);

function App() {
  const [i18nKey, setI18nKey] = useState(0);

  // Listen for locale changes and force I18nProvider to re-render
  useEffect(() => {
    const handleLanguageChange = () => {
      setI18nKey((prev) => prev + 1);
    };

    window.addEventListener("languagechange", handleLanguageChange);

    return () => {
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, []);

  const FadeUp = cssTransition({
    enter: "toast-animated toast-fadeInLeft",
    exit: "toast-animated toast-fadeOutLeft",
    collapse: false,
    duration: 300,
  });

  return (
    <>
      <I18nProvider i18n={i18n} key={i18nKey}>
        <HelmetProvider>
          <ConfigProvider
            // Dark mode
            theme={{
              algorithm: theme.lightAlgorithm,
              token: {
                colorPrimary: "#4e4e4e",
                fontFamily: "Inter, sans-serif",
              },
              components: {
                Select: {
                  zIndex: 1000000,
                  optionActiveBg: theme.verylight,
                  optionSelectedBg: theme.light,
                  optionPadding: "4px 8px",
                  optionHeight: 28,
                  activeShadow: "0 0 0 2px rgba(5, 5, 5, 0.1)",
                },
                Slider: {
                  dotSize: 0,
                  dotActiveBorderColor: "transparent",
                  handleSize: 15,
                  handleSizeHover: 1,
                  handleLineWidth: 0,
                  handleLineWidthHover: 2,
                  railSize: 3,
                  handleColor: theme.primary,
                  railColor: theme.primary,
                  handleActiveOutlineColor: theme.primary,
                },
                Tooltip: {
                  zIndexPopup: 10020,
                },
              },
            }}
          >
            <WhiteLabelProvider>
              <GrigoraVersioning>
                <QueryClientProvider client={queryClient}>
                  <Account>
                    <ToastContainer
                      limit={3}
                      transition={FadeUp}
                      position="bottom-left"
                      autoClose={2000}
                      hideProgressBar={true}
                      newestOnTop={false}
                      closeOnClick={false}
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                      style={{ zIndex: 99999999 }}
                      theme="light"
                      toastClassName={"g-toastify"}
                    />
                    {/* <AnimatePresence> */}
                    <RouterProvider router={router} />
                    {/* </AnimatePresence> */}
                  </Account>
                </QueryClientProvider>
              </GrigoraVersioning>
            </WhiteLabelProvider>
          </ConfigProvider>
        </HelmetProvider>
      </I18nProvider>
    </>
  );
}

export default App;
